
  import { defineComponent, onMounted, ref } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';
  import MonacoEditor from '@guolao/vue-monaco-editor';

  export default defineComponent({
    name: 'metatags-creating',
    components: {
      ErrorMessage,
      Field,
      Form,
      MonacoEditor,
    },
    setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const { can } = useAbility();
      const seoSchemaJson = ref('');

      const seoDataEn = ref<any>({
        title: '',
        desc: '',
        keywords: '',
        screen: '',
        og_title: '',
        og_description: '',
        og_url: '',
        og_image: '',
        og_image_alt: '',
        twitter_title: '',
        twitter_description: '',
        twitter_image: '',
        canonical_url: '',
        robots: '',
        schema_data: null,
      });

      const seoDataAr = ref<any>({
        title: '',
        desc: '',
        keywords: '',
        screen: '',
        og_title: '',
        og_description: '',
        og_url: '',
        og_image: '',
        og_image_alt: '',
        twitter_title: '',
        twitter_description: '',
        twitter_image: '',
        canonical_url: '',
        robots: '',
        schema_data: null,
      });

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      const metatagsSchema = Yup.object().shape({
        screen: Yup.string()
          .required(() => translate('SCREEN_IS_REQUIRED_FIELD'))
          .label('Screen'),
        titleAr: Yup.string()
          .required(() => translate('AR_TITLE_IS_REQUIRED_FIELD'))
          .label('Arabic Title'),
        descAr: Yup.string()
          .required(() => translate('AR_DESC_IS_REQUIRED_FIELD'))
          .label('Arabic Description'),
        titleEn: Yup.string()
          .required(() => translate('EN_TITLE_IS_REQUIRED_FIELD'))
          .label('English title'),
        descEn: Yup.string()
          .required(() => translate('EN_DESC_IS_REQUIRED_FIELD'))
          .label('English Description'),
      });

      const buildCombinedPayload = () => {
        const payload: any = {
          //en
          titleEn: seoDataEn.value.title,
          descEn: seoDataEn.value.desc,
          keywords_en: seoDataEn.value.keywords,
          screen: seoDataEn.value.screen,
          og_title_en: seoDataEn.value.og_title,
          og_description_en: seoDataEn.value.og_description,
          og_url: seoDataEn.value.og_url,
          og_image: seoDataEn.value.og_image,
          og_image_alt_en: seoDataEn.value.og_image_alt,
          twitter_title_en: seoDataEn.value.twitter_title,
          twitter_description_en: seoDataEn.value.twitter_description,

          //ar
          titleAr: seoDataAr.value.title,
          descAr: seoDataAr.value.desc,
          keywords_ar: seoDataAr.value.keywords,
          og_title_ar: seoDataAr.value.og_title,
          og_description_ar: seoDataAr.value.og_description,
          og_image_alt_ar: seoDataAr.value.og_image_alt,
          twitter_title_ar: seoDataAr.value.twitter_title,
          twitter_description_ar: seoDataAr.value.twitter_description,

          //common
          twitter_image: seoDataEn.value.twitter_image,
          canonical_url: seoDataEn.value.canonical_url,
          robots: seoDataEn.value.robots,
          schema_data: seoSchemaJson.value
            ? JSON.parse(seoSchemaJson.value)
            : null,
        };

        return payload;
      };

      const onSubmitCreate = async (values) => {
        if (submitButton.value) {
          submitButton.value.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        const payload = buildCombinedPayload();

        try {
          await store.dispatch(Actions.CREATE_MAIN_PAGE_SEO, payload);

          const [errorName] = Object.keys(store.getters.getErrors);
          const error = store.getters.getErrors[errorName];

          if (error) {
            Swal.fire({
              text: translate(error[0]),
              icon: 'error',
              buttonsStyling: false,
              confirmButtonText: translate('tryAgainExcl'),
              customClass: {
                confirmButton: 'btn fw-bold btn-light-danger',
              },
            });
          } else {
            Swal.fire({
              text: translate('SUCCESSFULLY_CREATED_METATAG'),
              icon: 'success',
              buttonsStyling: false,
              confirmButtonText: translate('ok'),
              customClass: {
                confirmButton: 'btn btn-light-primary',
              },
            }).then(() => {
              router.push({ name: 'metatags-listing' });
            });
          }
        } catch (err) {
          console.error('Create error:', err);
          Swal.fire({
            text: translate('An error occurred while creating metatag'),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
        } finally {
          submitButton.value?.removeAttribute('data-kt-indicator');
          if (submitButton.value) submitButton.value.disabled = false;
        }
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('metatagssCreating'), [
          translate('settings'),
        ]);
      });

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        onSubmitCreate,
        submitButton,
        translate,
        metatagsSchema,
        goBack,
        can,
        seoDataEn,
        seoDataAr,
        seoSchemaJson,
      };
    },
  });
